import { nextTick, type ComputedRef } from "vue";
import type { IEpoch } from "../../interfaces/IEpoch";
import type { ITranslations } from "../../interfaces/ITranslations";

import {
  decodeHtmlEntities,
  scrollWindowToElTop,
  freezeScrollDuring,
} from "../../services/vrak/domHelpersService";
import { readDocumentCssPx } from "../../services/readDocumentCssPx";

import {
  glideIconToMomentCenter,
  setRandomFadePositionOnAllIcons,
  fadeTimelineIcons,
  fadeTimeline,
  ICON_CLOSE_GLIDE_MS,
} from "../../services/vrak/timelineIconsService";
import timelineRandomVisualEffectService
  from "@features/Pages/TimelineMapPage/_js/app/services/vrak/timelineRandomVisualEffectService";

export function useEpochToggle(translations: ComputedRef<ITranslations>) {
  async function openEpoch(
    epochElement: HTMLElement,
    epoch: IEpoch,
    opts?: { scrollOffset?: number }
  ) {
    const transitionTimeout = 600;

    const closedHeight = epochElement.offsetHeight;
    epochElement.style.height = `${closedHeight}px`;
    epochElement.classList.add("epoch--is-opening");

    const moments = Array.from(
      epochElement.querySelectorAll<HTMLElement>(".moment")
    );
    const icons = moments
      .map((ev) => ev.querySelector<HTMLElement>(".timeline-icon"))
      .filter(Boolean) as HTMLElement[];

    epochElement.querySelectorAll<HTMLElement>(".epoch__content").forEach((c) => {
      c.style.display = "block";
      c.style.height = "auto";
      c.style.opacity = "1";
    });

    await nextTick();

    const glides = icons.map((icon) => {
      const momentEl = icon.closest<HTMLElement>(".moment");
      if (!momentEl) return Promise.resolve();

      icon.dataset.gliding = "1";
      icon.style.opacity = "1";
      icon.style.display = "inline-block";

      icon.classList.remove(
        "timeline-icon--vrak-on",
        "timeline-icon--event-on"
      );
      const isWreck = !!icon.closest(".moment--type-wreck");
      icon.classList.add(
        isWreck ? "timeline-icon--vrak-off" : "timeline-icon--event-off"
      );

      return glideIconToMomentCenter(icon, momentEl, transitionTimeout);
    });

    await Promise.all(glides);

    epochElement.classList.remove("epoch--is-opening");
    epochElement.classList.add("epoch--is-open");

    // Reset icon styles
    icons.forEach((icon) => {
      delete icon.dataset.gliding;

      icon.classList.forEach((cls) => {
        if (
          cls.startsWith("timeline-icon--vrak-off") ||
          cls.startsWith("timeline-icon--event-off")
        ) {
          icon.classList.remove(cls);
        }
      });

      const isWreck = !!icon.closest(".moment--type-wreck");
      icon.classList.add(
        isWreck ? "timeline-icon--vrak-on" : "timeline-icon--event-on"
      );

      icon.style.removeProperty("position");
      icon.style.removeProperty("left");
      icon.style.removeProperty("top");
      icon.style.removeProperty("transition");
      icon.style.removeProperty("display");
      icon.style.removeProperty("opacity");
    });

    epochElement
      .querySelectorAll<HTMLElement>(".moment.moment--type-wreck")
      .forEach((el) => {
        el.setAttribute("tabindex", "0");
        el.style.opacity = "1";
      });

    await nextTick();

    requestAnimationFrame(() => {
      const heading = epochElement.querySelector(
        ".epoch__text h2"
      ) as HTMLElement | null;
      const targetEl = heading ?? epochElement;

      const offset =
        (window.innerWidth >= 1260 ? 130 : 95) +
        readDocumentCssPx("--disturbance-height", 0);

      scrollWindowToElTop(targetEl, {
        offset: opts?.scrollOffset ?? offset,
        behavior: "smooth",
      });

      epochElement.style.height = "auto";
    });
  }

  function toggleEpoch(epoch: IEpoch, event: MouseEvent) {
    event.preventDefault();

    const epochElement = document.getElementById(`epoch-id-${epoch.id}`);
    if (!epochElement) { return; }

    const epochContent = epochElement.querySelector(
      ".epoch__content"
    ) as HTMLElement | null;
    const epochButton = epochElement.querySelector(
      ".epoch__toggle-btn"
    ) as HTMLElement | null;

    if (!epochContent || !epochButton) { return; }

    // Close epoch
    if (epochElement.classList.contains("epoch--is-open")) {
      epochButton.textContent = translations.value.show;
      epochButton.setAttribute(
        "aria-label",
        `${translations.value.show} ${epoch.epochYearName}`
      );
      epochButton.setAttribute("aria-pressed", "false");
      epochButton.focus?.({ preventScroll: true });

      epochElement.classList.add("epoch--is-closing");

      const iconsInEpoch = Array.from(
        epochElement.querySelectorAll<HTMLElement>(
          `.timeline-icon[data-epochid="${epoch.id}"]`
        )
      );

      const centers = new Map<
        HTMLElement,
        {
          x: number;
          y: number;
          isWreck: boolean;
          parent: HTMLElement | null;
          nextSibling: ChildNode | null;
        }
      >();

      iconsInEpoch.forEach((icon) => {
        const moment = icon.closest<HTMLElement>(".moment");
        if (!moment) { return; }

        const r = icon.getBoundingClientRect();
        centers.set(icon, {
          x: Math.round(r.left + r.width / 2),
          y: Math.round(r.top + r.height / 2),
          isWreck: moment.classList.contains("moment--type-wreck"),
          parent: icon.parentElement,
          nextSibling: icon.nextSibling,
        });
      });

      const rect = epochElement.getBoundingClientRect();
      const lockedHeight = epochElement.offsetHeight;

      const spacer = document.createElement("div");
      const cs = getComputedStyle(epochElement);
      spacer.style.height = `${lockedHeight}px`;
      spacer.style.width = `${rect.width}px`;
      spacer.style.margin = cs.margin;
      spacer.style.boxSizing = cs.boxSizing || "border-box";
      spacer.style.pointerEvents = "none";
      epochElement.parentElement?.insertBefore(spacer, epochElement);

      const originalStyles = {
        position: epochElement.style.position,
        top: epochElement.style.top,
        left: epochElement.style.left,
        width: epochElement.style.width,
        height: epochElement.style.height,
        zIndex: epochElement.style.zIndex,
        margin: epochElement.style.margin,
      };

      epochElement.style.position = "fixed";
      epochElement.style.top = `${rect.top}px`;
      epochElement.style.left = `${rect.left}px`;
      epochElement.style.width = `${rect.width}px`;
      epochElement.style.height = `${lockedHeight}px`;
      epochElement.style.zIndex = "10";
      epochElement.style.margin = "0";
      epochElement.style.overflow = "hidden";

      void epochElement.offsetHeight;

      iconsInEpoch.forEach((icon) => {
        const c = centers.get(icon);
        if (!c) { return; }

        icon.classList.forEach((cls) => {
          if (
            cls.startsWith("timeline-icon--vrak-on") ||
            cls.startsWith("timeline-icon--event-on")
          ) {
            icon.classList.remove(cls);
          }
        });

        if (c.isWreck) {
          icon.classList.remove("timeline-icon--event-off");
          icon.classList.add("timeline-icon--vrak-off");
        } else {
          icon.classList.remove("timeline-icon--vrak-off");
          icon.classList.add("timeline-icon--event-off");
        }

        const csIcon = getComputedStyle(icon);
        icon.style.width = csIcon.width;
        icon.style.height = csIcon.height;
        icon.style.backgroundImage = csIcon.backgroundImage;
        icon.style.backgroundSize = csIcon.backgroundSize;
        icon.style.backgroundRepeat = csIcon.backgroundRepeat;
        icon.style.backgroundPosition = csIcon.backgroundPosition;

        document.body.appendChild(icon);

        icon.dataset.gliding = "1";
        icon.style.position = "fixed";
        icon.style.display = "inline-block";
        icon.style.opacity = "1";
        icon.style.transform = "translate(-50%, -50%)";
        icon.style.transition =
          "left .6s cubic-bezier(.22,.61,.36,1), top .6s cubic-bezier(.22,.61,.36,1)";
        icon.style.left = `${c.x}px`;
        icon.style.top = `${c.y}px`;
        icon.style.zIndex = "9999";
      });

      requestAnimationFrame(() => {
        iconsInEpoch.forEach((icon, i) => {
          const half = iconsInEpoch.length / 2;
          const toLeft =
            timelineRandomVisualEffectService.getRandomLeftPosition(i < half);
          const toTop =
            timelineRandomVisualEffectService.getRandomTopPosition();

          icon.style.left = `${toLeft}px`;
          icon.style.top = `${toTop}px`;
        });
      });

      setTimeout(() => {
        freezeScrollDuring(() => {
          epochElement.classList.remove("epoch--is-open", "epoch--is-closing");

          epochElement
            .querySelectorAll<HTMLElement>(".moment")
            .forEach((el) => el.setAttribute("tabindex", "-1"));

          epochElement
            .querySelectorAll<HTMLElement>(".epoch__content")
            .forEach((c) => {
              c.style.display = "flex";
            });

          epochContent.style.height = "0";

          iconsInEpoch.forEach((icon) => {
            const c = centers.get(icon);
            delete icon.dataset.gliding;

            icon.style.removeProperty("transition");
            icon.style.removeProperty("transform");
            icon.style.removeProperty("display");
            icon.style.removeProperty("opacity");
            icon.style.removeProperty("width");
            icon.style.removeProperty("height");
            icon.style.removeProperty("background-image");
            icon.style.removeProperty("background-size");
            icon.style.removeProperty("background-repeat");
            icon.style.removeProperty("background-position");
            icon.style.removeProperty("z-index");

            if (c?.parent) {
              if (c.nextSibling && c.nextSibling.parentNode === c.parent) {
                c.parent.insertBefore(icon, c.nextSibling);
              } else {
                c.parent.appendChild(icon);
              }
            }
          });

          epochElement.style.position = originalStyles.position || "";
          epochElement.style.top = originalStyles.top || "";
          epochElement.style.left = originalStyles.left || "";
          epochElement.style.width = originalStyles.width || "";
          epochElement.style.height = originalStyles.height || "";
          epochElement.style.zIndex = originalStyles.zIndex || "";
          epochElement.style.margin = originalStyles.margin || "";
          epochElement.style.overflow = "";

          spacer.parentElement?.insertBefore(epochElement, spacer);
        });

        requestAnimationFrame(() => {
          freezeScrollDuring(() => {
            spacer.remove();
          });
          setRandomFadePositionOnAllIcons();
          fadeTimelineIcons();
          fadeTimeline();

          epochButton.focus?.({ preventScroll: true });
        });
      }, ICON_CLOSE_GLIDE_MS);
    } else {
      // Open epoch
      epochButton.textContent = decodeHtmlEntities(translations.value.close);
      epochButton.setAttribute(
        "aria-label",
        `${translations.value.close} ${epoch.epochYearName}`
      );
      epochButton.setAttribute("aria-pressed", "true");
      epochButton.focus?.({ preventScroll: true });

      const offset =
        (window.innerWidth >= 1260 ? 130 : 95) +
        readDocumentCssPx("--disturbance-height", 0);
      openEpoch(epochElement, epoch, { scrollOffset: offset });

      setTimeout(() => {
        setRandomFadePositionOnAllIcons();
        fadeTimelineIcons();
      }, 800);
    }
  }

  function onToggleEpoch(payload: { event: MouseEvent; epoch: IEpoch }) {
    toggleEpoch(payload.epoch, payload.event);
  }

  return {
    onToggleEpoch,
  };
}
